/**
 * Delivery Responses Modal
 *
 * For studio orgs: shows when broadcasters accept or reject deliveries.
 * Follows the ReviewResultsModal pattern.
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from 'reactstrap';
import moment from 'moment';
import { useOrganizationStore } from '../../store/organization.store';
import { useNotificationsStore } from '../../store/notifications.store';
import { filterByEnabledSince } from '../../services/notificationFilter';

interface DeliveryResponse {
  id: string;
  type?: 'accepted' | 'rejected';
  jobId: string;
  deliveryId: string;
  deliveryCode: string;
  broadcasterOrgId: string;
  broadcasterOrgName: string;
  rejectedByName?: string;
  acceptedByName?: string;
  rejectionReason?: string;
  rejectedAt: number;
}

interface DeliveryRejectionsModalProps {
  firebase: any;
}

const DeliveryRejectionsModal: React.FC<DeliveryRejectionsModalProps> = ({ firebase }) => {
  const [rejections, setRejections] = useState<DeliveryResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [dismissingIds, setDismissingIds] = useState<Set<string>>(new Set());
  const [dismissingAll, setDismissingAll] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  const { currentOrgId } = useOrganizationStore();

  const isOpen = useNotificationsStore(state => state.showDeliveryRejectionsModal);
  const setIsOpen = useNotificationsStore(state => state.setShowDeliveryRejectionsModal);
  const setDeliveryRejectionsCount = useNotificationsStore(state => state.setDeliveryRejectionsCount);

  useEffect(() => {
    setHasAutoOpened(false);
  }, [currentOrgId]);

  useEffect(() => {
    if (!currentOrgId || !firebase?.subscribeDeliveryRejectionsForOrg) {
      setRejections([]);
      setDeliveryRejectionsCount(0);
      return;
    }

    setLoading(true);
    const unsubscribe = firebase.subscribeDeliveryRejectionsForOrg(
      currentOrgId,
      (fetched: DeliveryResponse[]) => {
        const enabledSince = useNotificationsStore.getState().inAppEnabledSince;
        const filtered = filterByEnabledSince('deliveryRejections', fetched, enabledSince, (n) => n.rejectedAt);
        setRejections(filtered);
        setDeliveryRejectionsCount(filtered.length);
        setLoading(false);

        if (!hasAutoOpened && fetched.length > 0) {
          setIsOpen(true);
          setHasAutoOpened(true);
        }
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId, firebase]);

  useEffect(() => {
    if (isOpen && rejections.length === 0 && !loading) {
      setIsOpen(false);
    }
  }, [rejections.length, isOpen, loading, setIsOpen]);

  const handleDismiss = async (rejection: DeliveryResponse) => {
    if (!currentOrgId || !firebase) return;
    setDismissingIds(prev => new Set(prev).add(rejection.id));
    try {
      await firebase.dismissDeliveryRejection(currentOrgId, rejection.id);
    } catch (error) {
      console.error('Error dismissing rejection:', error);
    } finally {
      setDismissingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(rejection.id);
        return newSet;
      });
    }
  };

  const handleDismissAll = async () => {
    if (!currentOrgId || !firebase) return;
    setDismissingAll(true);
    try {
      await Promise.all(
        rejections.map(r => firebase.dismissDeliveryRejection(currentOrgId, r.id))
      );
    } catch (error) {
      console.error('Error dismissing all rejections:', error);
    } finally {
      setDismissingAll(false);
    }
  };

  const handleSkip = () => setIsOpen(false);

  if (!isOpen || rejections.length === 0) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleSkip}
      style={{ maxWidth: '600px', margin: '1.75rem auto' }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={handleSkip}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button className="close" onClick={handleSkip} style={{
            color: 'var(--color-accent)', opacity: 1, textShadow: 'none',
            background: 'none', border: 'none', fontSize: '1.75rem', fontWeight: 'normal', cursor: 'pointer',
          }}>
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>
          <i className="tim-icons icon-send mr-2" style={{ color: 'var(--color-accent)' }}></i>
          Delivery Responses ({rejections.length})
        </span>
      </ModalHeader>

      <ModalBody style={{
        backgroundColor: 'var(--chatbot-color-1)',
        color: '#ffffff',
        padding: '1rem',
        maxHeight: '60vh',
        overflowY: 'auto',
      }}>
        {loading ? (
          <div className="text-center py-4">
            <Spinner style={{ color: 'var(--color-accent)' }} />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {rejections.map((rejection) => {
              const isDismissing = dismissingIds.has(rejection.id);
              const isAccepted = rejection.type === 'accepted';
              return (
                <div key={rejection.id} style={{
                  padding: '1rem',
                  backgroundColor: isAccepted ? 'rgba(80, 200, 120, 0.05)' : 'rgba(255, 80, 80, 0.05)',
                  borderRadius: '8px',
                  border: `1px solid ${isAccepted ? 'rgba(80, 200, 120, 0.15)' : 'rgba(255, 80, 80, 0.15)'}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <div>
                      <div style={{ color: isAccepted ? '#50c878' : '#ff5050', fontWeight: '600', fontSize: '0.9rem' }}>
                        {isAccepted ? 'Delivery Acknowledged' : 'Delivery Rejected'}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
                        <strong style={{ color: '#fff' }}>{rejection.broadcasterOrgName}</strong>
                        {(rejection.rejectedByName || rejection.acceptedByName) && <> by {rejection.rejectedByName || rejection.acceptedByName}</>}
                        {isAccepted && (
                          <span style={{ display: 'block', color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', marginTop: '0.15rem' }}>
                            Confirmed receipt — spots and campaign are in their library.
                          </span>
                        )}
                        {!isAccepted && (
                          <span style={{ display: 'block', color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', marginTop: '0.15rem' }}>
                            Spots and campaign have been removed from their library.
                          </span>
                        )}
                      </div>
                    </div>
                    <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>
                      {rejection.rejectedAt ? moment(rejection.rejectedAt).format('DD/MM/YYYY HH:mm') : ''}
                    </span>
                  </div>

                  {rejection.rejectionReason && (
                    <div style={{
                      padding: '0.5rem 0.75rem', marginBottom: '0.5rem',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)', borderRadius: '4px',
                      color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontStyle: 'italic',
                    }}>
                      "{rejection.rejectionReason}"
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      size="sm"
                      className="btn-primary-custom"
                      onClick={() => handleDismiss(rejection)}
                      disabled={isDismissing || dismissingAll}
                      style={{ fontSize: '0.75rem', padding: '3px 10px' }}
                    >
                      <span style={{ position: 'relative', zIndex: 2 }}>
                        {isDismissing ? <Spinner size="sm" /> : 'Dismiss'}
                      </span>
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ModalBody>

      <ModalFooter style={{
        backgroundColor: 'var(--chatbot-color-2)',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        justifyContent: 'space-between',
        padding: '0.75rem 1rem',
      }}>
        <Button className="btn-info-custom" onClick={handleSkip} size="sm">
          <span style={{ position: 'relative', zIndex: 2 }}>Remind Me Later</span>
        </Button>
        <Button
          className="btn-primary-custom"
          onClick={handleDismissAll}
          disabled={dismissingAll || rejections.length === 0}
          size="sm"
        >
          <span style={{ position: 'relative', zIndex: 2 }}>
            {dismissingAll ? (
              <><Spinner size="sm" className="mr-1" /> Dismissing...</>
            ) : (
              `Dismiss All (${rejections.length})`
            )}
          </span>
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DeliveryRejectionsModal;
